import { EventEmitter } from 'eventemitter3';
import { Room } from '../room';
import { SignalCommand, SignalEvent } from './constant';
import SignalChannel from './signal-channel';
import { CustomMessage, CustomMessageData, ILogger, loggerManager, RoomEvent, User } from 'trtc-js-sdk-core';
import { delay } from 'trtc-js-sdk-core/src/utils/utils';

export const enum CustomMessageManagerEvent {
  MESSAGE = 'message'
}

interface CustomMessageManagerEventType {
  [CustomMessageManagerEvent.MESSAGE]: [CustomMessage];
}

// 获取随机 seq 值。
// 若 seq 从 0 开始，发了 n 个消息后，再重新进房，会导致 seq 又从 0 开始。这时接收端会收到比之前更小的 seq。有可能会误判为消息乱序了。
// 取随机的 seq 值可以降低这个 case 的概率。当接收端收到的 seq 间隔超过 300，则认为对端重新进房重新发送了。
function getRandomSeq() {
  return Math.floor(Math.random() * 16383);
}

export class CustomMessageManager extends EventEmitter<CustomMessageManagerEventType> {
  // 收到的 seq 序号差距超过 300，则认为远端用户重新进房，重新生成了 seq。
  static SEQ_INTERVAL = 300;
  log: ILogger;
  cmdIdSeqMap: Map<number, number> = new Map();
  // Map<userId_cmdId, { cachedMessageMap: Map<seq, {}>, lastSeq: number}
  messageMap: Map<string, { cachedMessageMap: Map<number, { message: CustomMessage, timeoutId?: number }>, lastSeq: number }> = new Map();
  constructor(public room: Room, public signalChannel: SignalChannel) {
    super();

    this.log = loggerManager.createLogger({
      parent: room.getLogger(),
      id: 'cmm',
      userId: room.userId
    });
    this.onReceiveMsg = this.onReceiveMsg.bind(this);
    signalChannel.on(SignalEvent.RECEIVE_CUSTOM_MSG, this.onReceiveMsg);

    // 远端用户退房，清理 messageMap 中的缓存
    this.room.on(RoomEvent.PEER_LEAVE, ({ userId: remoteUserId  }) => {
      [...this.messageMap.keys()].forEach(key => {
        const userId = key.split('_').slice(0, -1)
          .join('_');
        if (userId === remoteUserId) {
          this.messageMap.delete(key);
        }
      });
    });
  }

  send({ cmdId, data }: CustomMessageData) {
    const seq = this.cmdIdSeqMap.get(cmdId) || getRandomSeq();

    const message = {
      cmdId,
      msg: btoa(String.fromCharCode(...new Uint8Array(data))),
      // ws 是可靠传输的，后台服务器是不可靠传输，此处默认设置为可靠保序，暂不需支持不可靠、不保序，没有太大意义。
      ordered: true,
      reliable: true,
      streamSeq: seq
    };
    this.cmdIdSeqMap.set(cmdId, seq + 1);
    this.signalChannel.send(SignalCommand.SEND_CUSTOM_MSG, message);
    this.log.debug(`send custom msg: ${JSON.stringify(message)}`);
  }

  // 1. 第一次收到 msg，缓存 100ms，100 ms 后抛出所有缓存列表中的消息。规避首条消息乱序丢消息问题，但无法 100% 规避（考虑到首条消息无法判断是否乱序，以及考虑及时性不能缓存太久）。
  // 2. 非首次收到 msg
  //  2.1 若 seq < lastSeq，则丢弃
  //  2.2 若 seq === lastSeq，不可能，如果出现则是 bug
  //  2.3 若 seq === lastSeq + 1，抛事件
  //  2.4 若 seq > lastSeq + 1，则缓存 5s，若 5s 内收齐了之前的消息，则抛事件；否则在超时后再抛出。
  onReceiveMsg(event: any) {
    const { data } = event.data;

    const userId = this.room.tinyIdToUserIdMap.get(data.srcTinyId);
    if (userId) {
      const customMessage: CustomMessage = {
        userId,
        cmdId: data.cmdId,
        seq: data.streamSeq,
        // base64 后转成 arraybuffer，用 charCodeAt 可以解决 atob 不支持中文的问题
        // https://juejin.cn/post/7246747232997621816
        data: Uint8Array.from(atob(data.msg), c => c.charCodeAt(0)).buffer
      };
      if (data.ordered) {
        const key = `${userId}_${customMessage.cmdId}`;
        let value = this.messageMap.get(key);
        // 第一次收到消息，缓存 100ms 的消息。
        if (!value || value.lastSeq === 0) {
          if (!value) {
            value = { lastSeq: 0, cachedMessageMap: new Map() };
            this.messageMap.set(key, value);
            // 第一条消息缓存 100ms，规避首条消息乱序导致丢消息的问题
            setTimeout(() => this.emitMessage(customMessage, true), 100);
          }
          value!.cachedMessageMap.set(customMessage.seq, { message: customMessage });
        } else if (Math.abs(value.lastSeq - customMessage.seq) > CustomMessageManager.SEQ_INTERVAL) {
          this.messageMap.set(key, { lastSeq: customMessage.seq, cachedMessageMap: new Map() });
          this.emitMessage(customMessage);
        } else {
          if (customMessage.seq > value.lastSeq) {
            if (customMessage.seq === value.lastSeq + 1) {
              this.emitMessage(customMessage);
            } else if (!value.cachedMessageMap.has(customMessage.seq)) {
              // 5s 没收到数据，则 setTimeout 5s 后
              const timeoutId = setTimeout(() => this.emitMessage(customMessage, true), 5000);
              value.cachedMessageMap.set(customMessage.seq, { message: customMessage, timeoutId });
            }
          } else {
            // 收到 seq 比 lastSeq 小的消息，丢弃
            this.log.debug(`drop message ${customMessage.userId}-${customMessage.cmdId}-${customMessage.seq}`);
          }
        }
      } else {
        this.emit(CustomMessageManagerEvent.MESSAGE, customMessage);
      }
    } else { // 自定义消息先于 peer-join 收到，此时拿不到 userId（吐槽一下后台信令。。。），最多缓存 2s 等 peer-join 后再抛出自定义消息。
      this.log.warn(`receive msg from unknown user, wait peer-join tinyId: ${data.srcTinyId}`);
      const onRemoteUserJoined = (user: User) => {
        if (user.tinyId === data.srcTinyId) {
          this.room.off(RoomEvent.PEER_JOIN, onRemoteUserJoined);
          this.onReceiveMsg(event);
        }
      };
      this.room.on(RoomEvent.PEER_JOIN, onRemoteUserJoined);
      delay(2000).then(() => this.room.off(RoomEvent.PEER_JOIN, onRemoteUserJoined));
    }
  }

  emitMessage(customMessage: CustomMessage, emitSortedMessageList = false) {
    const value = this.messageMap.get(`${customMessage.userId}_${customMessage.cmdId}`);
    let message = customMessage;

    if (value) {
      // 收消息 5s 超时，此时抛出缓存消息队列中 seq 最小的消息。
      if (emitSortedMessageList) {
        const sortedMessageList = [...value.cachedMessageMap.values()].sort((a, b) => a.message.seq - b.message.seq);
        if (sortedMessageList[0]) {
          message = sortedMessageList[0].message;
        }
      }
      if (value.lastSeq !== 0 && message.seq - value.lastSeq > 1) {
        // 检测到丢消息了。value.lastSeq -> message.seq 中间的消息没收到。
        this.log.debug(`msg lost userId: ${message.userId} seq: ${value.lastSeq} -> ${message.seq}`);
      }
      value.lastSeq = message.seq;
      clearTimeout(value.cachedMessageMap.get(message.seq)?.timeoutId);
      value.cachedMessageMap.delete(message.seq);
    }
    this.log.debug(`receive custom msg: ${JSON.stringify(message)}`);
    this.emit(CustomMessageManagerEvent.MESSAGE, message);
    // 抛出消息后，检查是否有 seq + 1 的消息，有则递归执行 emitMesaage 直至没有为止。
    const nextMessage = value?.cachedMessageMap.get(message.seq + 1);
    if (nextMessage) {
      this.emitMessage(nextMessage.message);
    }
  }
}
